import React from "react";
import {
    CategoryOutlined,
    // The nineteenth species, 2026-10-27: an inbound demand, before anyone has decided what to
    // offer. An inbox tray rather than a quote slip -- the quote is what it BECOMES.
    MoveToInboxOutlined,
    DescriptionOutlined,
    GavelOutlined,
    HubOutlined,
    LocalShippingOutlined,
    PrecisionManufacturingOutlined,
    ReceiptLongOutlined,
    StorefrontOutlined,
    WarehouseOutlined,
    Inventory2Outlined,
    DashboardCustomizeOutlined,
    SchoolOutlined,
    RequestQuoteOutlined,
    EngineeringOutlined,
    ReportProblemOutlined,
    QuizOutlined,
    ScienceOutlined,
    BiotechOutlined,
    FolderSpecialOutlined} from "@mui/icons-material";

/**
 * The Foundation first-class objects.
 *
 * They share ONE backend and one set of components: every object is an ObjectRevision row
 * distinguished by ObjectClassification, so a page, a create dialog and a detail view are
 * parameterised by an entry here rather than duplicated nine times.
 *
 * `code` MUST match raw.ACCESS_OBJECT_TYPE.CODE. It is what the classification column stores, what
 * the type list is filtered by, and what the privilege guard passes to
 * effective_privileges_for_record -- a different spelling here makes grants silently match nothing.
 * Customers cannot add classifications; this list changes only when the code does.
 */

export type ObjectClassification = {
    /** ACCESS_OBJECT_TYPE.CODE. Stored on OBJECT and OBJECT_REVISION. */
    code: string;
    /** "Document" — dialog titles, the detail header. */
    singular: string;
    /** "Documents" — the section header, the sidebar, the list page. */
    plural: string;
    /** Path segment. The detail route is `${route}/:usid`. */
    route: string;
    icon: React.ElementType;
    /**
     * Whether creating one of these should also ask which OBJECT TYPES it is offered on.
     *
     * Only a FORM has this, and the flag lives here rather than as a `code === "FORM"` test inside
     * CreateObject so the shared dialog stays shared. TWO DIFFERENT OBJECT TYPES ARE IN PLAY when a
     * form is created and conflating them is the trap: the type it IS (classification FORM, which
     * issues its number) and the types it is FOR (everything else). This is the second.
     */
    offeredOnObjectTypes?: boolean;
    /**
     * Filed INSIDE a project rather than standing on its own, so it gets no entry in the left rail.
     *
     * ED'S RULE, 2026-10-26: "A project is a container and it will have visibility controls and
     * will be controlled via the object/object revision lifecycle rules. Products, Quote Request,
     * Quote Responses, Contracts and Build Plans are ingredients to a project and will not have
     * visibility controls or a lifecycle process."
     *
     * SO THIS FLAG IS ABOUT NAVIGATION ONLY, and deliberately not about either of the other two
     * things that sentence says. Dropping the visibility controls and the lifecycle from these
     * four is a change to the SERVER -- `effective_privileges_for_record`, the approval matrix, the
     * revision chain -- and doing it from a front-end flag would mean a screen that stopped asking
     * while the backend went on enforcing. That half is the next piece of work, not this one.
     *
     * THE ENTRY STAYS IN THIS LIST, which is the part worth saying out loud: it carries the label,
     * the route, the icon and the privilege code, and ObjectRoute resolves `/object/<usid>` through
     * it into `${route}/${usid}`. Remove the entry and every link a project tab draws to one of its
     * own members stops resolving.
     */
    filedInProject?: boolean;
};

export const OBJECT_CLASSIFICATIONS: ObjectClassification[] = [
    {code: "Document", singular: "Document", plural: "Documents", route: "/documents", icon: DescriptionOutlined},
    {code: "Item", singular: "Item", plural: "Items", route: "/items", icon: CategoryOutlined},
    // Plural and singular are the same word; kept as separate fields rather than derived, because
    // English pluralisation rules would get this and "Equipment" wrong.
    // A LOT IS ITS OWN SPECIES SINCE 2026-10-30 (Ed: "We will change Inventory to be a means of
    // tracking multiple lots of a particular item"). Before that a lot was an Inventory record of
    // type LOT-; Peak's moved. Lots sit before Inventory in the rail because the lot is the thing
    // people handle and Inventory is the view over many of them.
    {code: "Lot", singular: "Lot", plural: "Lots", route: "/lots", icon: Inventory2Outlined},
    // A RECEIVABLE IS ONE RECEIPT OF GOODS AGAINST A PURCHASE ORDER, the twenty-first species
    // (2026-10-30, Ed: "Lots and Receivables needs to be on the sidebar menu"). It sits with the
    // lot because a lot is made from a receipt.
    {code: "Receivable", singular: "Receivable", plural: "Receivables", route: "/receivables", icon: MoveToInboxOutlined},
    {code: "Inventory", singular: "Inventory", plural: "Inventory", route: "/inventory", icon: WarehouseOutlined},
    {code: "Equipment", singular: "Equipment", plural: "Equipment", route: "/equipment", icon: PrecisionManufacturingOutlined},
    {code: "Customer", singular: "Customer", plural: "Customers", route: "/customers", icon: StorefrontOutlined},
    {code: "Supplier", singular: "Supplier", plural: "Suppliers", route: "/suppliers", icon: LocalShippingOutlined},
    {code: "Distributor", singular: "Distributor", plural: "Distributors", route: "/distributors", icon: HubOutlined},
    // THE FOLDER THE COMMERCIAL ARC HANGS FROM, and the eighteenth species as of 2026-10-27.
    //
    // IT WAS NOT IN THIS REGISTRY UNTIL THEN, and that absence is what Ed found: a project was a
    // row in raw.PROJECT with its own status column, so `/object/<usid>` did not serve it, it had a
    // hand-written detail page with hand-rolled buttons, and the Permissions and History panes had
    // nothing to draw because there were no `project:*` privileges and no OBJECT_AUDIT rows. Being
    // here is what gives it the standard list page, the standard detail page and all seven panes.
    //
    // NOT `filedInProject`: this is the folder, not one of its ingredients. It therefore takes no
    // group below and the rail's unclaimed fallback gives it its own top-level entry, which is the
    // right shape for it -- and replaces the entry AppsSidebar used to write by hand.
    {code: "Project", singular: "Project", plural: "Projects",
     route: "/projects", icon: FolderSpecialOutlined},
    {code: "Contract", filedInProject: true, singular: "Contract", plural: "Contracts", route: "/contracts", icon: GavelOutlined},
    {code: "PurchaseOrder", singular: "Purchase Order", plural: "Purchase Orders", route: "/purchase-orders", icon: ReceiptLongOutlined},
    // A FORM IS ONE OF THESE. Its OBJECT_REVISION.SCHEMA is the definition, and it has the same
    // revision chain, attribution, audit trail and persona ownership as a Document -- so it gets the
    // same list page, filters and counts rather than a bespoke screen that would have to rebuild
    // each of them. Its DETAIL route is overridden in AppRoutes to open the designer instead of the
    // generic record page.
    {code: "FORM", singular: "Form", plural: "Forms", route: "/forms", icon: DashboardCustomizeOutlined,
     offeredOnObjectTypes: true},
    // A TRAINING CURRICULUM IS ONE OF THESE TOO. It says who must be trained on what and how often --
    // a controlled document in its own right, which an auditor asks to see approved -- so it wants the
    // revision chain, the lifecycle, the approval matrix and the persona ownership that come with being
    // an object, rather than a bespoke screen that would have to grow each of them.
    //
    // PLURAL IS "Curricula", which English gets right and a naive pluraliser does not -- the same reason
    // Inventory and Equipment carry theirs explicitly.
    {code: "TrainingCurriculum", singular: "Training Curriculum", plural: "Training Curricula",
     route: "/training-curricula", icon: SchoolOutlined},

    // ── ADDED 2026-10-24, AND THE DELAY IS THE POINT ──────────────────────────────────────────
    //
    // These four species shipped with their services, their doors, their public API and their
    // documentation, and were absent from THIS LIST -- which is the only thing that gives a
    // species a page. So Phases 6, 7, 10 and 11 were reachable by an integrator holding a
    // credential and by nobody sitting in the product, and nothing said so: every deploy signal
    // was green, because the backend was.
    //
    // ONE LINE EACH. That is what makes the omission worth a comment rather than a shrug -- the
    // cost of the gap was never the work, it was that nobody was looking at this file.
    //
    // AND A LIST PAGE IS NOT THE WHOLE PHASE. The generic ObjectDetail shows a record's fields; it
    // cannot show a quote's priced lines, a plan's schedule and workforce, a quality record's
    // workflow, or a questionnaire's answers and their sources. Those are compositions and each
    // needs its own panel. This makes the RECORDS findable, which is the half that was free.
    // RENAMED 2026-10-27, ON ED'S INSTRUCTION, AND THE CODE DID NOT MOVE WITH IT. `PreparedQuote`
    // is stored in OBJECT_CLASSIFICATION on every existing record, in PROJECT_MEMBER.MEMBER_TYPE,
    // in ACCESS_OBJECT_TYPE.CODE that the guard resolves against, and in the seventeen privilege
    // codes roles are granted by -- a name is what people read, a code is what the machine
    // resolves, and only one of them was wrong. "Quote response" is the source product's own word
    // and the better one: this is the ANSWER to a quote request, and a reader holding both needs
    // to tell them apart at a glance.
    //
    // THE SEVENTEEN PRIVILEGE NAMES IN THE DATABASE MOVED IN LOCKSTEP, because they have to:
    // `objectPrivilegeNames` below builds "${singular}: ${verb}" and the Permissions tab looks
    // each one up in raw.PRIVILEGE BY NAME. Rename here and leave those, and that tab asks for
    // "Quote Response: View", finds nothing, and shows an empty pane for all seventeen verbs with
    // no error anywhere. See docs/Schema/migrations/20261027_quote_response_rename.sql.
    // THE NINETEENTH SPECIES, 2026-10-27. It was not in this registry before because it was not a
    // classification: `raw.QUOTE_REQUEST` was a plain row, and every path that handled the five
    // things filed in a project had to special-case this one. Ed's rule is that all five are
    // Object/ObjectRevision types; being here is what gives it the standard detail page, the audit
    // trail, attachments and a form.
    //
    // `filedInProject`, LIKE ITS FOUR SIBLINGS: no rail entry, no Visibility pane, no Permissions
    // pane, and no lifecycle buttons. The folder is the authority.
    {code: "QuoteRequest", filedInProject: true, singular: "Quote Request", plural: "Quote Requests",
     route: "/quote-requests", icon: MoveToInboxOutlined},
    {code: "PreparedQuote", filedInProject: true, singular: "Quote Response", plural: "Quote Responses",
     route: "/quote-responses", icon: RequestQuoteOutlined},
    {code: "BuildPlan", filedInProject: true, singular: "Build Plan", plural: "Build Plans",
     route: "/build-plans", icon: EngineeringOutlined},
    // THE CLASSIFICATION IS QualityRecord AND IT HOLDS TWO KINDS -- an Issue (ISS-) and a CAPA
    // (CAPA-) are both this one classification, distinguished by their object TYPE. So one entry
    // lists both, which is correct and is also the thing a reader assumes wrongly: there is no
    // "Issue" classification to add.
    {code: "QualityRecord", singular: "Quality Record", plural: "Quality Records",
     route: "/quality-records", icon: ReportProblemOutlined},
    {code: "Questionnaire", singular: "Questionnaire", plural: "Questionnaires",
     route: "/questionnaires", icon: QuizOutlined},

    // NO Product ENTRY, since 2026-10-29. A product was the seventeenth species from 2026-10-25 to
    // then; Ed retired it ("replace it with an Item with Object Type = PRT-"), so a product is an
    // Item whose type is PRT- and whose form is the Product form. It still has a kind in a project
    // ("Products", with its own tab, lane and count): see util/projectMembers.ts, which carries the
    // kind's icon and its type now that no classification does.
    {code: "Experiment", singular: "Experiment", plural: "Experiments",
     route: "/experiments", icon: BiotechOutlined},
];

/**
 * Privilege VERBS, as the display half of each seeded privilege name.
 *
 * These must match the labels in migrations/20260809_object_privileges.sql exactly: the Permissions
 * tab looks a privilege up by NAME, so a label that differs by a character renders an empty block
 * rather than an error. The matching CODE (`document:edit`) is what the guards check and what
 * t1/v1/object_access returns.
 */
const PRIVILEGE_VERBS = [
    "View", "Edit", "Delete", "Revise", "Release", "Deprecate", "Cancel", "Void",
    "Add relationship", "Delete relationship", "Manage access", "View access",
];

/**
 * The privilege display names for one classification, e.g. "Document: Edit".
 *
 * Privileges are per classification rather than shared across all nine, because a role granting
 * edit on Documents must not thereby grant edit on Purchase Orders.
 */
export function objectPrivilegeNames(classification: ObjectClassification): string[] {
    return PRIVILEGE_VERBS.map(verb => `${classification.singular}: ${verb}`);
}

/**
 * The privilege CODE a guard checks for a verb on this classification, e.g. "document:create".
 *
 * Built from the ACCESS_OBJECT_TYPE spelling, not `code`: a Form's classification is stored as FORM,
 * and lower-camelling that gives "fORM:create", which nobody holds. The server resolves the same way
 * (object_privilege_guard._registered_code).
 */
export function objectPrivilegeCode(classification: ObjectClassification, verb: string): string {
    const registered = classification.code === "FORM" ? "Form" : classification.code;
    return `${registered.charAt(0).toLowerCase()}${registered.slice(1)}:${verb}`;
}

export function classificationByCode(code?: string | null): ObjectClassification | undefined {
    return OBJECT_CLASSIFICATIONS.find(c => c.code === code);
}

export function classificationByRoute(route?: string | null): ObjectClassification | undefined {
    return OBJECT_CLASSIFICATIONS.find(c => c.route === route);
}
